import https from './https';
import { isBulkPackagingKey } from './productUtils';

export interface CartItem {
    id: string;
    name: string;
    image: string;
    ram?: string;
    subtotal: number;
    cuttoff: string;
    no: number;
    priceA?: number;
    purchaseMode?: string;
    productId?: string;
}

export const syncLocalStorageCart = async () => {
    const isAuth = typeof window !== 'undefined' && !!localStorage.getItem('token');
    if (!isAuth) return;

    const cartStr = localStorage.getItem('honeywill_cart');
    if (!cartStr) return;

    try {
        const items = JSON.parse(cartStr);
        if (Array.isArray(items) && items.length > 0) {
            // Filter out items that are already synced, or have undefined ID
            const unsSyncedItems = items.filter((item: any) => 
                item && 
                item.id && 
                String(item.id) !== 'undefined' && 
                !item.isSynced
            );

            if (unsSyncedItems.length === 0) {
                // Nothing to sync
                return;
            }

            // Fetch the current API cart to see what's already there
            let currentApiItems: any[] = [];
            try {
                const res = await https.get('cart');
                currentApiItems = parseApiCart(res);
            } catch (e) {
                console.error("Error fetching current cart during sync:", e);
            }

            for (const item of unsSyncedItems) {
                const productId = item.productId || item.id.split('_')[0];
                const mode = item.purchaseMode || 'coil';
                const exists = currentApiItems.some(apiItem => 
                    (apiItem.productId === productId || apiItem.id.split('_')[0] === productId) && 
                    (apiItem.purchaseMode || 'coil') === mode
                );

                if (!exists) {
                    // Sync this item to the api
                    await https.post('cart/add', {
                        product_id: productId,
                        quantity: item.purchaseMode === 'box' ? 1 : item.no,
                        purchase_mode: mode
                    });
                }
            }

            // Once synced successfully, fetch the fresh API cart and save it as synced
            const res = await https.get('cart');
            const parsed = parseApiCart(res);
            const syncedItems = parsed.map(item => ({ ...item, isSynced: true }));
            localStorage.setItem('honeywill_cart', JSON.stringify(syncedItems));
            window.dispatchEvent(new Event('cartUpdated'));
        }
    } catch (e) {
        console.error("Error syncing cart to api:", e);
    }
};

export const parseApiCart = (apiData: any): CartItem[] => {
    let items: any[] = [];

    if (apiData) {
        if (Array.isArray(apiData)) {
            items = apiData;
        } else if (Array.isArray(apiData.data)) {
            items = apiData.data;
        } else if (apiData.data && Array.isArray(apiData.data.items)) {
            items = apiData.data.items;
        } else if (Array.isArray(apiData.items)) {
            items = apiData.items;
        }
    }

    return items.map((item: any) => {
        const prod = item.product_id || item.product || {};
        const pid = typeof prod === 'object' ? (prod._id || prod.id) : item.product_id;
        const name = prod.name || item.name || 'Soft Copper Pipe';
        let mode = item.purchase_mode || 'coil';
        if (mode === 'single') {
            mode = 'coil';
        }
        
        // Calculate price and original price based on mode
        const isBox = mode === 'box';

        const getRollsCount = (p: any) => {
            if (p.rolls_count) return p.rolls_count;
            if (Array.isArray(p.specifications)) {
                const spec = p.specifications.find((s: any) => s && isBulkPackagingKey(s.key));
                if (spec && spec.value) {
                    const match = String(spec.value).match(/\d+/);
                    if (match) return parseInt(match[0], 10);
                }
            }
            return 1;
        };
        const rollsCount = getRollsCount(prod);

        // Helper to resolve single price
        let singlePrice = 0;
        if (Array.isArray(prod.price)) {
            const singleObj = prod.price.find((p: any) => p && (p.type === 'single' || p.type === 'coil'));
            singlePrice = singleObj ? singleObj.price : (prod.price[0]?.price || 0);
        } else if (typeof prod.price === 'number') {
            singlePrice = prod.price;
        } else {
            singlePrice = Number(prod.price || item.price) || 0;
        }

        // Helper to resolve single mrp
        let singleMrp = singlePrice;
        if (Array.isArray(prod.price)) {
            const singleObj = prod.price.find((p: any) => p && (p.type === 'single' || p.type === 'coil'));
            singleMrp = singleObj && typeof singleObj.mrp === 'number' ? singleObj.mrp : singlePrice;
        } else if (prod.mrp) {
            singleMrp = prod.mrp;
        } else if (prod.originalPrice) {
            singleMrp = prod.originalPrice;
        } else if (item.mrp) {
            singleMrp = item.mrp;
        } else {
            singleMrp = singlePrice * 1.15;
        }

        // Helper to resolve box price
        let boxPrice = 0;
        if (Array.isArray(prod.price)) {
            const boxObj = prod.price.find((p: any) => p && p.type === 'box');
            if (boxObj) boxPrice = boxObj.price;
        }
        if (!boxPrice) {
            boxPrice = prod.box_price || (singlePrice * rollsCount);
        }

        // Helper to resolve box mrp
        let boxMrp = boxPrice;
        if (Array.isArray(prod.price)) {
            const boxObj = prod.price.find((p: any) => p && p.type === 'box');
            if (boxObj && typeof boxObj.mrp === 'number') boxMrp = boxObj.mrp;
        }
        if (!boxMrp || boxMrp === boxPrice) {
            boxMrp = prod.box_mrp || (singleMrp * rollsCount);
        }

        const priceVal = isBox ? boxPrice : singlePrice;
        const mrpVal = isBox ? boxMrp : singleMrp;
        const displayName = isBox ? `${name} (Box)` : `${name} (Coil)`;
        const images = prod.images || prod.image || item.images || [];
        const imageUrl = Array.isArray(images) ? (images[0] || "/images/products/placeholder.png") : (images || "/images/products/placeholder.png");

        return {
            id: `${pid}_${mode}`,
            name: displayName,
            image: imageUrl,
            ram: item.ram || "Standard",
            subtotal: priceVal * (item.quantity || item.no || 1),
            cuttoff: `₹${Math.ceil(mrpVal).toLocaleString()}`,
            no: item.quantity || item.no || 1,
            priceA: priceVal,
            purchaseMode: mode,
            productId: String(pid)
        };
    });
};

let activeFetchPromise: Promise<CartItem[]> | null = null;

export const fetchCartItems = async (): Promise<CartItem[]> => {
    const isAuth = typeof window !== 'undefined' && !!localStorage.getItem('token');
    if (!isAuth) {
        const cartStr = localStorage.getItem('honeywill_cart');
        if (!cartStr) return [];
        try {
            const items = JSON.parse(cartStr);
            if (Array.isArray(items)) {
                const validItems = items.filter((item: any) => item && item.id && String(item.id) !== 'undefined');
                if (validItems.length !== items.length) {
                    localStorage.setItem('honeywill_cart', JSON.stringify(validItems));
                }
                return validItems;
            }
        } catch (e) {
            console.error(e);
        }
        return [];
    }

    if (activeFetchPromise) {
        return activeFetchPromise;
    }

    activeFetchPromise = (async () => {
        try {
            const res = await https.get('cart');
            const parsed = parseApiCart(res);
            const syncedItems = parsed.map(item => ({ ...item, isSynced: true }));
            localStorage.setItem('honeywill_cart', JSON.stringify(syncedItems));
            return parsed;
        } catch (e) {
            console.error("Error fetching cart from API, falling back to local storage:", e);
            const cartStr = localStorage.getItem('honeywill_cart');
            if (!cartStr) return [];
            try {
                const items = JSON.parse(cartStr);
                if (Array.isArray(items)) {
                    const validItems = items.filter((item: any) => item && item.id && String(item.id) !== 'undefined');
                    if (validItems.length !== items.length) {
                        localStorage.setItem('honeywill_cart', JSON.stringify(validItems));
                    }
                    return validItems;
                }
            } catch (err) {
                console.error(err);
            }
            return [];
        } finally {
            activeFetchPromise = null;
        }
    })();

    return activeFetchPromise;
};
